import { z } from 'zod';
/**
 * Every machine-readable error `code` in the system, closed. The PWA and the
 * admin web switch on these — a code not in this list can never be emitted
 * by a conforming server. See the package README, "Ambiguity resolutions",
 * for the additions beyond what the docs name explicitly and the reasoning
 * behind each HTTP status assignment below.
 */
export const ERROR_CODES = [
    // ---- generic (03-API-CONTRACT.md §9 explicit list) ----
    'VALIDATION_FAILED',
    'UNAUTHENTICATED',
    'FORBIDDEN',
    'NOT_FOUND',
    'CONFLICT',
    'RATE_LIMITED',
    'INTERNAL_ERROR',
    'NOT_IMPLEMENTED',
    'WEAK_PIN',
    'INVALID_PIN',
    'PIN_CHANGE_REQUIRED',
    'ILLEGAL_STATUS_TRANSITION',
    'INVALID_CONFIG_KEY',
    'EMPLOYEE_NUMBER_TAKEN',
    'MEMBERSHIP_EXISTS',
    // ---- auth / login (03-API-CONTRACT.md §1) ----
    'INVALID_CREDENTIALS',
    'ACCOUNT_LOCKED',
    'ACCOUNT_HARD_LOCKED',
    'ACCOUNT_INACTIVE',
    'DEVICE_NOT_REGISTERED',
    // Additions: D-05 describes refresh-token rotation and reuse detection but
    // 03-API-CONTRACT.md never names the failure codes for /auth/refresh.
    'INVALID_REFRESH_TOKEN',
    'REFRESH_TOKEN_REUSED',
    // ---- punch (03-API-CONTRACT.md §3, 04-PUNCH-ENGINE.md) ----
    'OUTSIDE_PUNCH_WINDOW',
    'NO_SHIFT_SCHEDULED',
    'CONTRACT_INACTIVE',
    'STALE_OPEN_PUNCH',
    'INVALID_CAPTURE_TIME',
    'NO_OPEN_PUNCH',
    'INVALID_PUNCH_OUT_TIME',
    'OUTSIDE_GEOFENCE',
    // ---- scheduling (03-API-CONTRACT.md §5) ----
    'SHIFT_OUTSIDE_CONTRACT_WINDOW',
    'WORKER_ALREADY_SCHEDULED',
    'SHIFT_HAS_PUNCHES',
    'SHIFT_IN_PAST',
    // Additive, 2026-09-04: the shift is in a terminal status (completed,
    // cancelled, no_show) — history, not a plan. No edit, assign, or cancel.
    'SHIFT_CLOSED',
    // Phase 1 change batch (2026-08-27), Track B: assigning a user who is not
    // an active `worker` member of the contract / outside site scope / a bad
    // slot. Shape errors stay VALIDATION_FAILED; this is a business-rule
    // refusal on an otherwise well-formed request.
    'USER_NOT_ASSIGNABLE',
    // ---- delete refusals (not named in the docs; judged necessary for
    // `DELETE /sites/:id` and `DELETE /zones/:id`, which have no other
    // documented refusal code) ----
    'SITE_HAS_ZONES',
    'SITE_HAS_SHIFTS',
    'ZONE_HAS_SHIFTS',
    // Phase 1 change batch (2026-08-27), Track E: the generic business-rule
    // refusal for a well-formed request the domain still refuses, when no
    // more specific code applies.
    'BUSINESS_RULE_VIOLATION',
    // Phase 2 batch (2026-08-27), foundation: the last two framework codes
    // already on the wire. `PAYLOAD_TOO_LARGE` is a body over the 1 MB limit
    // (`bootstrap.ts`); `SERVICE_UNAVAILABLE` is `GET /health/db` when
    // PostgreSQL is unreachable. 03-API-CONTRACT.md §0's table has no 413 or
    // 503 row, but the API emits both, so they belong in the closed enum — a
    // code a client cannot switch on must never reach the wire.
    'PAYLOAD_TOO_LARGE',
    'SERVICE_UNAVAILABLE',
    // Phase 4 (system roles, 2026-08-29): the three refusals of
    // `POST /users/:id/role`. Never your own account; never a role above your
    // own rank (`ROLE_RANK`); and a worker still holding future shift or series
    // assignments cannot be moved off `worker` — the supervisor reassigns first,
    // nothing is silently unassigned (`details: { shifts, series }`).
    'CANNOT_CHANGE_OWN_ROLE',
    'ROLE_ABOVE_CALLER',
    'USER_HAS_FUTURE_ASSIGNMENTS',
    // Phase 4 review (2026-08-29, F2): the role is system-wide, so changing it
    // is a decision about every contract the person can reach. A caller who
    // holds only part of the target's scope cannot make it — only a platform
    // admin can, or a caller whose scope covers the target's entirely.
    'USER_SCOPE_EXCEEDS_CALLER',
    // Phase 5 (tasks, 2026-09-02). `TASKS_INCOMPLETE` is the only one a worker
    // ever sees: punch OUT with a blocking task still pending and no override
    // reason, carrying `details.openTasks[{ id, title }]` so the PWA can name
    // what is left rather than just refusing. The override (T-07) is always
    // accepted — nobody is ever trapped clocked in.
    'TASKS_INCOMPLETE',
    'TASK_ALREADY_TERMINAL',
    'TASK_SHIFT_NOT_ASSIGNABLE',
    'TASK_SCOPE_MISMATCH',
    'TASK_PHOTO_REQUIRED',
    'TASK_NOT_ASSIGNED',
];
export const ErrorCodeSchema = z.enum(ERROR_CODES);
/**
 * Maps every `ErrorCode` to the HTTP status it must be raised with, per
 * `03-API-CONTRACT.md` §0's status table:
 *
 * 400 malformed · 401 no/expired session · 403 not permitted / out of scope ·
 * 404 not found · 409 invariant conflict · 413 body over the size limit ·
 * 422 business rule refusal · 429 rate limited · 500 internal ·
 * 501 not implemented (Phase 0 stub) · 503 a critical dependency is down.
 *
 * The `Record<ErrorCode, ...>` shape means TypeScript itself refuses to
 * compile if a code is ever added to `ERROR_CODES` without a status here.
 */
export const ERROR_HTTP_STATUS = {
    VALIDATION_FAILED: 400,
    INVALID_CONFIG_KEY: 400,
    UNAUTHENTICATED: 401,
    INVALID_CREDENTIALS: 401,
    ACCOUNT_LOCKED: 401,
    ACCOUNT_HARD_LOCKED: 401,
    ACCOUNT_INACTIVE: 401,
    INVALID_REFRESH_TOKEN: 401,
    REFRESH_TOKEN_REUSED: 401,
    FORBIDDEN: 403,
    DEVICE_NOT_REGISTERED: 403,
    PIN_CHANGE_REQUIRED: 403,
    ROLE_ABOVE_CALLER: 403,
    USER_SCOPE_EXCEEDS_CALLER: 403,
    NOT_FOUND: 404,
    PAYLOAD_TOO_LARGE: 413,
    CONFLICT: 409,
    STALE_OPEN_PUNCH: 409,
    ILLEGAL_STATUS_TRANSITION: 409,
    EMPLOYEE_NUMBER_TAKEN: 409,
    MEMBERSHIP_EXISTS: 409,
    SITE_HAS_ZONES: 409,
    SITE_HAS_SHIFTS: 409,
    ZONE_HAS_SHIFTS: 409,
    WORKER_ALREADY_SCHEDULED: 409,
    SHIFT_HAS_PUNCHES: 409,
    // 422, not 409: nothing about the request is malformed and no row
    // conflicts with it — the date has simply gone by, and re-sending it
    // will never help.
    SHIFT_IN_PAST: 422,
    SHIFT_CLOSED: 409,
    CANNOT_CHANGE_OWN_ROLE: 409,
    USER_HAS_FUTURE_ASSIGNMENTS: 409,
    TASK_ALREADY_TERMINAL: 409,
    TASK_SHIFT_NOT_ASSIGNABLE: 409,
    OUTSIDE_PUNCH_WINDOW: 422,
    NO_SHIFT_SCHEDULED: 422,
    CONTRACT_INACTIVE: 422,
    INVALID_CAPTURE_TIME: 422,
    NO_OPEN_PUNCH: 422,
    INVALID_PUNCH_OUT_TIME: 422,
    OUTSIDE_GEOFENCE: 422,
    SHIFT_OUTSIDE_CONTRACT_WINDOW: 422,
    WEAK_PIN: 422,
    INVALID_PIN: 422,
    USER_NOT_ASSIGNABLE: 422,
    BUSINESS_RULE_VIOLATION: 422,
    TASKS_INCOMPLETE: 422,
    TASK_SCOPE_MISMATCH: 422,
    TASK_PHOTO_REQUIRED: 422,
    TASK_NOT_ASSIGNED: 422,
    RATE_LIMITED: 429,
    INTERNAL_ERROR: 500,
    NOT_IMPLEMENTED: 501,
    SERVICE_UNAVAILABLE: 503,
};
//# sourceMappingURL=error-codes.js.map